import React, { useState, useRef } from 'react';
import { Plus, X, Image } from 'lucide-react';
import { ThemeConfig, SpaceThing } from '../types';
import { firebaseService } from '../FirebaseService';

interface ThingsInSpaceProps {
  theme: ThemeConfig;
  things: SpaceThing[];
  setThings: React.Dispatch<React.SetStateAction<SpaceThing[]>>;
}

const ThingsInSpace: React.FC<ThingsInSpaceProps> = ({ theme, things, setThings }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newThingName, setNewThingName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddThing = async () => {
    if (!newThingName.trim()) {
      setError('Please enter a name for the thing');
      return;
    }

    // Check if user is authenticated
    const currentUser = firebaseService.auth.currentUser;
    if (!currentUser) {
      setError('You must be logged in to add things');
      return;
    }


    setIsAdding(true);
    setError(null);

    try {
      const newThingData = { name: newThingName, image: null };
      const newThing: SpaceThing = {
        id: new Date().toISOString(),
        ...newThingData
      };
      
      
      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out after 10 seconds. This usually means Firestore is not responding. Check:\n1. Network connection\n2. Firestore rules\n3. Browser console for CORS/network errors')), 10000);
      });
      
      await Promise.race([
        firebaseService.saveSpaceThing(newThing),
        timeoutPromise
      ]);
      
      
      setThings([...things, newThing]);
      setNewThingName('');
      setShowAddModal(false);
      setError(null);
    } catch (err: any) {
      console.error('Error adding thing:', err);
      setError(err.message || 'Failed to add thing. Please check the browser console for details.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteThing = async (id: string) => {
    await firebaseService.deleteSpaceThing(id);
    setThings(things.filter(t => t.id !== id));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, thingId: string) => {
    const file = e.target.files?.[0];
    if (file) {
      
      setUploadingId(thingId);
      const user = firebaseService.auth.currentUser;
      
      if (!user) {
        console.error('No authenticated user found');
        setError('You must be logged in to upload images');
        setUploadingId(null);
        return;
      }


      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        try {
          const dataUrl = reader.result as string;
          const imagePath = `users/${user.uid}/spaceThings/${thingId}/${file.name}`;
          
          
          const imageUrl = await firebaseService.uploadImage(dataUrl, imagePath);
          

          const updatedThing = { image: imageUrl };
          await firebaseService.saveSpaceThing({ id: thingId, name: things.find(t => t.id === thingId)!.name, ...updatedThing });

          setThings(prevThings =>
            prevThings.map(t => (t.id === thingId ? { ...t, image: imageUrl } : t))
          );
          setUploadingId(null);
        } catch (error: any) {
          console.error('Upload failed:', {
            message: error.message,
            code: error.code,
            stack: error.stack
          });
          setError(`Upload failed: ${error.message || 'Unknown error'}`);
          setUploadingId(null);
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setError('Failed to read file');
        setUploadingId(null);
      };
    }
  };

  const triggerImageUpload = (thingId: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('data-thing-id', thingId);
      fileInputRef.current.click();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${theme.text}`}>
          Things in My Space 🎨
        </h2>
        <button
          onClick={() => setShowAddModal(true)}
          className={`${theme.button} text-white py-2 px-4 rounded-lg text-sm transition-all flex items-center gap-2`}
        >
          <Plus className="w-4 h-4" />
          Add New Thing
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {things.map(thing => (
          <div key={thing.id} className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-4 shadow-lg ${theme.shadow} border ${theme.border} relative`}>
            <button
              onClick={() => handleDeleteThing(thing.id)}
              className={`absolute top-2 right-2 ${theme.accent} text-white p-1 rounded-full hover:opacity-80 transition-all`}
            >
              <X className="w-4 h-4" />
            </button>
            <div className={`aspect-square ${theme.secondary} rounded-xl mb-3 flex items-center justify-center overflow-hidden`}>
              {thing.image ? (
                <img src={thing.image} alt={thing.name} className="w-full h-full object-cover" />
              ) : (
                <Image className={`w-12 h-12 ${theme.textLight}`} />
              )}
            </div>
            <h3 className={`font-semibold ${theme.text} mb-2`}>{thing.name}</h3>
            <button
              onClick={() => triggerImageUpload(thing.id)}
              disabled={uploadingId === thing.id}
              className={`w-full ${theme.button} text-white py-2 px-4 rounded-lg text-sm transition-all disabled:opacity-50`}
            >
              {uploadingId === thing.id ? 'Uploading...' : 'Upload Photo'}
            </button>
          </div>
        ))}
      </div>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        className="hidden"
        onChange={(e) => {
          const thingId = e.currentTarget.getAttribute('data-thing-id');
          if (thingId) {
            handleImageUpload(e, thingId);
          }
        }}
      />

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-6 max-w-md w-full shadow-2xl`}>
            <h3 className={`text-xl font-bold ${theme.text} mb-4`}>Add New Thing</h3>
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            <input
              type="text"
              value={newThingName}
              onChange={(e) => {
                setNewThingName(e.target.value);
                setError(null);
              }}
              placeholder="Enter name..."
              className={`w-full p-3 border ${theme.border} rounded-lg mb-4 ${theme.text}`}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isAdding) {
                  handleAddThing();
                }
              }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setError(null);
                  setNewThingName('');
                }}
                disabled={isAdding}
                className={`flex-1 py-2 px-4 rounded-lg border ${theme.border} ${theme.text} hover:${theme.secondary} transition-all disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={handleAddThing}
                disabled={isAdding}
                className={`flex-1 ${theme.button} text-white py-2 px-4 rounded-lg transition-all disabled:opacity-50`}
              >
                {isAdding ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThingsInSpace;